import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { isEmpty } from "lodash"
import { GettextInterface } from "@indeed/indeed-gettext"
import { IJobInfo, IJobRequirement } from "../../common/utils/job-info"
import { constructSalaryString } from "../../common/utils/job-info/job-info-util"
import { selectorOf, thunkOf } from "../../common/utils/redux-helpers"
import { defaultAndLogError, hasKey, run, throwError, toTitleCase } from "../../common/utils/utils"
import {
    IDisplayItem,
    IMultiSelectDisplayItem,
    DisplayItem,
    MultiSelectDisplayItem,
    isMultiSelectDisplayItem,
    DislikedItemType,
    IPreferenceCategory,
} from "./feedback-form-model"

const initialState = {
    opts: run(() => {
        return [
            DisplayItem({
                key: "DISLIKED_JOBTYPE",
                iconCategory: "JOB_TYPE",
                field: "DISLIKED_JOBTYPE",
                value: "",
                unselectedText: "",
                selectedText: "",
                selected: false,
                isDisplayed: false,
            }),
        ]
    }),
    requirements: {
        certifications: [] as IDisplayItem[],
        educations: [] as IDisplayItem[],
        languages: [] as IDisplayItem[],
        licenses: [] as IDisplayItem[],
        skills: [] as IDisplayItem[],
        totalNumberOfRequirements: 0,
        totalNumberOfRequirementsSelected: 0,
    },
}

export function createOpts(optsProperties: IOptsProperties): (IDisplayItem | IMultiSelectDisplayItem)[] {
    const { job, locale, gettext } = optsProperties
    const isJapanese = locale.split("_")[0] === "ja"

    return [
        DisplayItem({
            key: "DISLIKED_NORMTITLE",
            iconCategory: "JOB_TITLE",
            field: "DISLIKED_NORMTITLE",
            unselectedText: toTitleCase(job.normTitleInfo?.normTitle ?? "") || defaultAndLogError(gettext("job title")),
            selectedText: gettext(
                "We'll show you fewer job titles like: [{0}|strongText]",
                toTitleCase(job.normTitleInfo?.normTitle ?? "")
            ),
            selected: false,
            isDisplayed: job.normTitleInfo?.normTitle != null,
        }),
        DisplayItem({
            key: "DISLIKED_COMPANY_NAME",
            iconCategory: "COMPANY",
            field: "DISLIKED_COMPANY_NAME",
            unselectedText: job.companyName ?? defaultAndLogError(gettext("company name")),
            selectedText: gettext("We'll show you fewer jobs from: [{0}|strongText]", job.companyName),
            selected: false,
            isDisplayed: job.companyName != null,
        }),
        MultiSelectDisplayItem({
            key: "multiselect-location",
            iconCategory: "LOCATION",
            field: "multiselect-location",
            unselectedText: locationStr(job.city, job.state, job.postalCode),
            // NOTE: selectedText is mutated in the reducer when the additional selections are toggled
            selectedText: locationStr(job.city, job.state, job.postalCode),
            value: `${job.city}, ${job.state} ${job.postalCode}`,
            selected: false,
            additionalSelections: [
                { key: "DISLIKED_CITY", displayText: job.city, selected: false },
                { key: "DISLIKED_STATE", displayText: job.state, selected: false },
                { key: "DISLIKED_POSTAL_CODE", displayText: job.postalCode, selected: false },
            ].filter(hasKey("displayText")),
            additionalSelectionsLabel: gettext("What about the location isn't right?"),
            isDisplayed: (!isEmpty(job.city) || !isEmpty(job.state)) && !job.isRemote,
        }),
        DisplayItem({
            key: "DISLIKED_EXTRACTED_PAY",
            iconCategory: "PAY",
            field: "DISLIKED_EXTRACTED_PAY",
            ...run(() => {
                const salaryText = constructSalaryString(gettext, job.salaryInfo, isJapanese)
                const updatedPayPreferencesText = gettext(
                    "[{0}|strongText] [br]Keep your [pay preferences|payPreferencesEl] up to date for best results.",
                    [salaryText]
                )
                return {
                    unselectedText: salaryText,
                    selectedText: updatedPayPreferencesText,
                }
            }),
            selected: false,
            isDisplayed: job.salaryInfo?.payRate != null && job.salaryInfo?.salaryType != null,
        }),
        DisplayItem({
            key: "DOESNT_WANT_REMOTE",
            iconCategory: "REMOTE",
            field: "DOESNT_WANT_REMOTE",
            unselectedText: gettext("Remote"),
            selectedText: gettext("We'll show you fewer jobs that are: [Remote|strongText]"),
            selected: false,
            isDisplayed: job.isRemote != null && job.isRemote,
        }),
        // JSXE-4566: hiding job type until there is a plan to use the field
        // ...(showJobType
        //     ? (
        //           job.jobTypes?.map((jobType) =>
        //               DisplayItem({
        //                   key: `DISLIKED_JOBTYPE-${jobType.suid}`,
        //                   iconCategory: "JOB_TYPE",
        //                   field: "DISLIKED_JOBTYPE",
        //                   value: jobType.suid,
        //                   unselectedText: jobType.displayLabel ?? defaultAndLogError(gettext("job type")),
        //                   selectedText: gettext(
        //                       "We'll show you fewer jobs that are: [{0}|strongText]",
        //                       jobType.displayLabel?.toLowerCase()
        //                   ),
        //                   selected: false,
        //                   isDisplayed: true,
        //               })
        //           ) ?? []
        //       ).slice(0, 4)
        //     : []),
    ].filter((it) => it.isDisplayed)
}

function getJobRequirements(
    gettext: GettextInterface["gettext"],
    type: DislikedItemType,
    iconCategory: IPreferenceCategory,
    requirements?: IJobRequirement[]
): IDisplayItem[] {
    return (
        requirements?.map((requirement) =>
            DisplayItem({
                key: `${type}-${requirement.suid}`,
                iconCategory,
                field: type,
                value: requirement.suid,
                unselectedText: requirement.label,
                selectedText: gettext("We'll show you fewer jobs that require: [{0}|strongText]", requirement.label),
                selected: false,
                isDisplayed: true,
            })
        ) ?? []
    )
}

export interface IOptsProperties {
    job: IJobInfo
    locale: string
    gettext: GettextInterface["gettext"]
}

// reducer

export const feedbackFormSlice = createSlice({
    name: "feedbackForm",
    initialState,
    reducers: {
        generateAndSetOpts(state, action: PayloadAction<IOptsProperties>) {
            state.opts = createOpts(action.payload)
        },
        setOpts(state, action: PayloadAction<(IDisplayItem | IMultiSelectDisplayItem)[]>) {
            state.opts = action.payload
        },
        toggleSelected(state, action: PayloadAction<number>) {
            state.opts[action.payload].selected = !state.opts[action.payload].selected
        },
        toggleAdditionalSelection(state, action: PayloadAction<{ additionalSelectionIdx: number; optionIdx: number }>) {
            const { optionIdx, additionalSelectionIdx } = action.payload
            const self = state.opts[optionIdx] as IMultiSelectDisplayItem
            self.additionalSelections[additionalSelectionIdx].selected = !self.additionalSelections[additionalSelectionIdx].selected
        },
        setSelectedLocationText(state, action: PayloadAction<{ job: IJobInfo; gettext: GettextInterface["gettext"] }>) {
            const { job, gettext } = action.payload
            const selection = state.opts.find((it) => it.field === "multiselect-location") ?? throwError("impossible state")
            if (!isMultiSelectDisplayItem(selection)) return

            const dislikesCity = selection.additionalSelections?.find((p) => p.key === "DISLIKED_CITY")?.selected ?? false
            const dislikesState = selection.additionalSelections?.find((p) => p.key === "DISLIKED_STATE")?.selected ?? false
            const dislikesPostalCode = selection.additionalSelections?.find((p) => p.key === "DISLIKED_POSTAL_CODE")?.selected ?? false

            const locStr = run(() => {
                switch ([dislikesCity, dislikesState, dislikesPostalCode].toString()) {
                    case [true, true, true].toString():
                    case [true, true, false].toString():
                    case [false, true, false].toString():
                    case [false, true, true].toString():
                        return `${job.state}` // state is the broadest selection, so if they select state at all, just display state
                    case [true, false, false].toString():
                        return `${job.city}, ${job.state}`
                    case [false, false, true].toString():
                        return `${job.postalCode}`
                    case [true, false, true].toString(): // city, state are both shown for clarity
                    case [false, false, false].toString(): // if they haven't selected anything, assume they mean the most specific location
                    default:
                        return locationStr(job.city, job.state, job.postalCode)
                }
            })

            selection.selectedText = gettext("We'll show you fewer jobs in: [{0}|strongText]", locStr)
            selection.value = locStr
        },
        generateAndSetJobRequirements(state, action: PayloadAction<{ job: IJobInfo; gettext: GettextInterface["gettext"] }>) {
            const { job, gettext } = action.payload
            const certificationRequirements = getJobRequirements(
                gettext,
                "DOESNT_HAVE_CERTIFICATION",
                "CERTIFICATION",
                job.certificationRequirements
            )
            const licenseRequirements = getJobRequirements(gettext, "DOESNT_HAVE_LICENSE", "CERTIFICATION", job.licenseRequirements)
            const educationRequirements = getJobRequirements(gettext, "DOESNT_HAVE_EDUCATION", "EDUCATION", job.educationRequirements)
            const languageRequirements = getJobRequirements(gettext, "DOESNT_KNOW_LANGUAGE", "LANGUAGE", job.languageRequirements)
            const skillRequirements = getJobRequirements(gettext, "DOESNT_HAVE_SKILL", "SKILL", job.skillRequirements)
            state.requirements = {
                certifications: certificationRequirements,
                licenses: licenseRequirements,
                educations: educationRequirements,
                languages: languageRequirements,
                skills: skillRequirements,
                totalNumberOfRequirements:
                    certificationRequirements.length +
                    licenseRequirements.length +
                    educationRequirements.length +
                    languageRequirements.length +
                    skillRequirements.length,
                totalNumberOfRequirementsSelected: 0,
            }
        },
        toggleCertificationSelected(state, action: PayloadAction<number>) {
            state.requirements.certifications[action.payload].selected = !state.requirements.certifications[action.payload].selected
        },
        toggleLicenseSelected(state, action: PayloadAction<number>) {
            state.requirements.licenses[action.payload].selected = !state.requirements.licenses[action.payload].selected
        },
        toggleEducationSelected(state, action: PayloadAction<number>) {
            state.requirements.educations[action.payload].selected = !state.requirements.educations[action.payload].selected
        },
        toggleLanguageSelected(state, action: PayloadAction<number>) {
            state.requirements.languages[action.payload].selected = !state.requirements.languages[action.payload].selected
        },
        toggleSkillSelected(state, action: PayloadAction<number>) {
            state.requirements.skills[action.payload].selected = !state.requirements.skills[action.payload].selected
        },
    },
})

const locationStr = (city?: string, state?: string, postalCode?: string): string => {
    return `${city ? `${city}` : ""}${(!isEmpty(city) && !isEmpty(state)) || (!isEmpty(city) && !isEmpty(postalCode)) ? "," : ""} ${
        state ?? ""
    } ${postalCode ?? ""}`.trim()
}

export const optsSelector = selectorOf<(IDisplayItem | IMultiSelectDisplayItem)[]>((s) =>
    s.feedbackForm.opts.filter((it) => it.isDisplayed)
)

export const requirementsSelector = selectorOf((s) => s.feedbackForm.requirements)
export const toggledRequirementsSelector = selectorOf((s) => {
    const { requirements } = s.feedbackForm
    return [
        ...requirements.licenses.filter((license) => license.selected),
        ...requirements.certifications.filter((certification) => certification.selected),
        ...requirements.educations.filter((education) => education.selected),
        ...requirements.languages.filter((language) => language.selected),
        ...requirements.skills.filter((skill) => skill.selected),
    ]
})

export const selectedFeedback = createSelector(optsSelector, (s) => s.filter((it) => it.selected))

// actions

export const toggleAdditionalLocationSelectionAction = thunkOf(
    "toggleAdditionalLocationSelection",
    (arg: { additionalSelectionIdx: number; optionIdx: number; gettext: GettextInterface["gettext"] }, thunkAPI) => {
        thunkAPI.dispatch(feedbackFormSlice.actions.toggleAdditionalSelection(arg))
        thunkAPI.dispatch(feedbackFormSlice.actions.setSelectedLocationText({ job: thunkAPI.getState().jobInfo, gettext: arg.gettext }))
    }
)

// exports

// Action creators are generated for each case reducer function
export const { actions } = feedbackFormSlice

export const feedbackFormReducer = feedbackFormSlice.reducer
